/**
 * Get the browser's timezone
 * @returns Timezone string (e.g., "America/Los_Angeles", "America/New_York")
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the timezone abbreviation (e.g., "PST", "EST", "CET")
 * @param date - Optional date to get abbreviation for (handles DST)
 * @returns Timezone abbreviation
 */
export function getTimezoneAbbreviation(date: Date = new Date()): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  });

  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find(part => part.type === 'timeZoneName');

  return timeZonePart?.value || 'Local';
}

/**
 * Convert UTC time (HH:MM) to local time
 * @param utcTime - Time in HH:MM format (UTC)
 * @param date - Optional date to use for DST calculation (defaults to today)
 * @returns Object with local time, timezone abbreviation, and day offset
 */
export function convertUTCTimeToLocal(
  utcTime: string,
  date: Date = new Date()
): { time: string; timezone: string; dayOffset: number } {
  if (!utcTime || !utcTime.includes(':')) {
    return { time: '', timezone: '', dayOffset: 0 };
  }

  const [hours, minutes] = utcTime.split(':').map(Number);

  // Create a UTC date with the specified time
  const utcDate = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes
    )
  );

  // Convert to local date
  const localDate = new Date(
    utcDate.toLocaleString('en-US', {
      timeZone: getBrowserTimezone(),
    })
  );

  // Calculate day offset
  const utcDay = utcDate.getUTCDate();
  const localDay = localDate.getDate();

  let dayOffset = 0;
  if (localDay !== utcDay) {
    // Check if it's next day or previous day
    const dayDiff = localDay - utcDay;

    // Handle month boundaries (e.g., UTC: Dec 1, Local: Nov 30)
    if (dayDiff > 1 || dayDiff < -1) {
      // Month boundary crossing
      dayOffset = dayDiff > 0 ? -1 : 1;
    } else {
      dayOffset = dayDiff;
    }
  }

  // Format to local time
  const localTimeString = localDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const timezone = getTimezoneAbbreviation(utcDate);

  return { time: localTimeString, timezone, dayOffset };
}

/**
 * Convert UTC date to local date string
 * @param utcDateString - Date in YYYY-MM-DD format
 * @returns Local date string in readable format
 */
export function convertUTCDateToLocal(utcDateString: string): string {
  if (!utcDateString) {
    return '';
  }

  const utcDate = new Date(utcDateString + 'T00:00:00Z');

  return utcDate.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get the timezone offset in hours
 * @returns Offset from UTC (e.g., -8 for PST, -5 for EST, +1 for CET)
 */
export function getTimezoneOffset(): number {
  return -new Date().getTimezoneOffset() / 60;
}

/**
 * Format timezone offset as string
 * @returns Formatted offset (e.g., "UTC-8", "UTC+1")
 */
export function getTimezoneOffsetString(): string {
  const offset = getTimezoneOffset();
  if (offset === 0) {
    return 'UTC';
  }
  const sign = offset > 0 ? '+' : '';
  return `UTC${sign}${offset}`;
}

/**
 * Format day offset for display
 * @param offset - Number of days offset (-1, 0, or +1)
 * @returns Formatted string (e.g., "+1 day", "-1 day", or empty)
 */
export function formatDayOffset(offset: number): string {
  if (offset === 0) {
    return '';
  }
  if (offset === 1) {
    return '+1 day';
  }
  if (offset === -1) {
    return '-1 day';
  }
  return '';
}
